import { logger } from '@beelday/common'
import {
	loadYouTubeIframeApi,
	YouTubeIframeApi,
	YouTubePlayer,
} from 'features/youtube-streaming/youtube-utils'
import { RefObject, useEffect, useRef } from 'react'
import type { MediaProgress } from 'room/common/media-progress'
import { monitorCorrection } from './model/monitor-correction'

const log = logger.create('presenter-mode')

/** How often the monitor looks at the projector's last report. */
export const MONITOR_LOOK_MS = 500
/**
 * After a jump the monitor's position takes a moment to settle (buffering):
 * looking again at once would jump again.
 */
export const MONITOR_SETTLE_MS = 2000

/**
 * The player's own controls and everything else that would let the trainer
 * work the monitor, instead of the remote, or show anything but the film.
 */
const MONITOR_PLAYER_VARS = {
	controls: 0,
	disablekb: 1,
	fs: 0,
	rel: 0,
	iv_load_policy: 3,
	cc_load_policy: 0,
	modestbranding: 1,
	playsinline: 1,
}

/**
 * The film the projector is playing, played again - silent - in an element of
 * the console, so the trainer sees what the room sees. Returns the ref of
 * that element; the player is built into it and follows `remote`, the
 * presentation window's last report, by the rules in
 * `model/monitor-correction.ts`. It plays nothing on its own: with no
 * report it stops.
 *
 * Silent because the projector's speakers play the sound, and two sources
 * would echo: the player is muted before it first plays, and stays muted.
 */
export const useVideoMonitor = (
	videoId: string | null,
	remote: MediaProgress | null
): RefObject<HTMLDivElement> => {
	const host = useRef<HTMLDivElement>(null)
	const latest = useRef(remote)
	latest.current = remote

	useEffect(() => {
		const element = host.current
		if (!videoId || !element) {
			return
		}
		let cancelled = false
		let player: YouTubePlayer | null = null
		let looking: number | undefined
		let settledAt = 0

		const look = (current: YouTubePlayer, yt: YouTubeIframeApi): void => {
			if (!current.isMuted()) {
				current.mute()
			}
			const now = Date.now()
			if (now < settledAt) {
				return
			}
			const state = current.getPlayerState()
			const correction = monitorCorrection({
				remote: latest.current,
				localPositionMs: current.getCurrentTime() * 1000,
				localPlaying:
					state === yt.PlayerState.PLAYING ||
					state === yt.PlayerState.BUFFERING,
				now,
			})
			switch (correction.type) {
				case 'play':
					current.playVideo()
					return
				case 'pause':
					current.pauseVideo()
					return
				case 'seek':
					settledAt = now + MONITOR_SETTLE_MS
					current.seekTo(correction.positionMs / 1000, true)
					return
				case 'none':
					return
			}
		}

		loadYouTubeIframeApi().then(
			api => {
				if (cancelled) {
					return
				}
				const yt = api as YouTubeIframeApi
				// The API replaces the element it is given with its frame; React's
				// own element stays put around it.
				const mount = document.createElement('div')
				element.appendChild(mount)
				const created: YouTubePlayer = new yt.Player(mount, {
					videoId,
					playerVars: {
						...MONITOR_PLAYER_VARS,
						origin: window.location.origin,
					},
					events: {
						onReady: () => {
							if (cancelled) {
								return
							}
							created.mute()
							created.getIframe().tabIndex = -1
							looking = window.setInterval(
								() => look(created, yt),
								MONITOR_LOOK_MS
							)
						},
					},
				})
				player = created
			},
			error =>
				log.warn('The video monitor could not load the YouTube player', error)
		)

		return () => {
			cancelled = true
			window.clearInterval(looking)
			player?.destroy()
			element.replaceChildren()
		}
	}, [videoId])

	return host
}
